import {
  CompareNode,
  RuleContext,
  RuleNode,
  SectionVisibleNode,
  TabVisibleNode,
} from '@navstation-portal/reporting/data-model/ui';
// The rule tree and the value tree hold each other: `compare` reads a value,
// and the `when` value node reads a rule. Each module names the other, and
// each call sits inside a function body, so neither needs the other while the
// modules load.
import { resolveValue } from './resolve-value';

/**
 * Evaluates a rule node to a boolean. Each node type has one branch. Add a
 * branch for each new node type.
 */
export function evaluate(node: RuleNode, context: RuleContext): boolean {
  switch (node.type) {
    case 'const':
      return node.value;
    case 'reportTypeIn':
      // A report with no type matches no list.
      return context.reportType !== null && node.reportTypes.includes(context.reportType);
    case 'previousReportTypeIn':
      // A vessel with no completed report matches no list.
      return context.previousReportType !== null && node.reportTypes.includes(context.previousReportType);
    case 'isInternalAdmin':
      return context.isInternalAdmin;
    case 'isAdmin':
      return context.isAdmin;
    case 'vesselFieldTruthy':
      // A missing vessel spec or a missing path is falsy.
      return isPathTruthy(context.vesselSpec, node.path);
    case 'vesselFieldIn':
      // A missing vessel spec or a missing path reads undefined, which no list
      // holds.
      return (node.values as unknown[]).includes(readVesselField(context, node.path));
    case 'tabVisible':
      return evaluateVisibilityRule(node.tab, readVisibilityRule(node, context), context);
    case 'sectionVisible':
      // A path that is not two parts is a template mistake, and false.
      if (node.path.split('.').length !== 2) {
        return false;
      }
      return evaluateVisibilityRule(node.path, readVisibilityRule(node, context), context);
    case 'reportFieldTruthy':
      // A missing form state, a missing path or a bad path is falsy.
      return !!readReportField(context, node.path);
    case 'carryOverFieldTruthy':
      // No previous report, a missing step or a bad path is falsy.
      return !!readCarryOverField(context, node.path);
    case 'reportFieldEquals':
      // A value the node cannot read matches nothing, so the node is false.
      return readReportField(context, node.path, node.valueKey) === node.equals;
    case 'compare':
      return compare(node, context);
    case 'not':
      return !evaluate(node.node, context);
    case 'and':
      // An empty list is true.
      return node.nodes.every((child) => evaluate(child, context));
    case 'or':
      // An empty list is false, the mirror of 'and'.
      return node.nodes.some((child) => evaluate(child, context));
    default:
      // Every node type has a branch above. This guards a new type added
      // without a branch: the compiler fails here.
      return assertNever(node);
  }
}

/**
 * Reads the rule a `tabVisible` or a `sectionVisible` node evaluates, so the
 * debug UI can say when the node found none. Undefined when no template loads,
 * when the template holds no rule there, or when a section path is not two
 * parts.
 */
export function readVisibilityRule(
  node: TabVisibleNode | SectionVisibleNode,
  context: RuleContext
): RuleNode | undefined {
  if (node.type === 'tabVisible') {
    return context.template?.tabs?.[node.tab]?.isTabVisible;
  }
  const keys = node.path.split('.');
  if (keys.length !== 2) {
    return undefined;
  }
  const [tabId, sectionId] = keys;
  return context.template?.tabs?.[tabId]?.sections?.[sectionId]?.isSectionVisible;
}

/**
 * The tab and section rules that `evaluate` is inside now, by rules path. A
 * rule that reaches one of them again is a loop.
 */
const visibilityRulesInProgress = new Set<string>();

/**
 * Evaluates the visibility rule of a tab or a section for `tabVisible` and
 * `sectionVisible`. A missing rule shows the tab or section, as the form does.
 *
 * A rule that reaches its own tab or section again is a template mistake, and
 * false. Without this guard, the stack overflows. `evaluate` is synchronous,
 * so one set for the module holds the path of the one evaluation that runs.
 */
function evaluateVisibilityRule(rulesPath: string, rule: RuleNode | undefined, context: RuleContext): boolean {
  if (!rule) {
    return true;
  }
  if (visibilityRulesInProgress.has(rulesPath)) {
    return false;
  }
  visibilityRulesInProgress.add(rulesPath);
  try {
    return evaluate(rule, context);
  } finally {
    visibilityRulesInProgress.delete(rulesPath);
  }
}

function assertNever(node: never): never {
  throw new Error(`Unknown rule node type: ${String((node as { type: string }).type)}`);
}

/**
 * Compares two value nodes.
 *
 * It answers false whenever it has nothing to compare: a side with no value,
 * and two sides that hold different kinds of value. A date against a number
 * answers a question nobody asked, and the operators of JavaScript would answer
 * it anyway, because they turn a date into a number first.
 *
 * Every order fails in the same direction for the same reason, so no template
 * gets a true from a field the user has not filled.
 */
function compare(node: CompareNode, context: RuleContext): boolean {
  const left = comparable(resolveValue(node.left, context));
  const right = comparable(resolveValue(node.right, context));
  if (left === undefined || right === undefined || left.kind !== right.kind) {
    return false;
  }
  switch (node.op) {
    case 'lt':
      return left.value < right.value;
    case 'lte':
      return left.value <= right.value;
    case 'gt':
      return left.value > right.value;
    case 'gte':
      return left.value >= right.value;
    default:
      // Every order has a branch above. This guards a new order added without
      // one: the compiler fails here.
      return assertNeverOrder(node.op);
  }
}

/** A value as a number, with the kind it came from, so a date never meets a number. */
function comparable(value: number | Date | undefined): { kind: 'number' | 'date'; value: number } | undefined {
  if (typeof value === 'number') return { kind: 'number', value };
  if (value instanceof Date) return { kind: 'date', value: value.getTime() };
  return undefined;
}

function assertNeverOrder(op: never): never {
  throw new Error(`Unknown compare order: ${String(op)}`);
}

/**
 * Walks a dotted path from an object, for example 'position_list.voyage_number',
 * and reports whether the value at the end is truthy. A missing step, so a
 * missing field or a null on the way, is falsy.
 */
function isPathTruthy(source: unknown, path: string): boolean {
  return !!readPath(source, path.split('.'));
}

/**
 * Reads the value a `vesselFieldTruthy` node tests. The debug UI shows it, so a
 * reader can see the input as well as the answer. It is undefined when a step
 * of the path is missing.
 */
export function readVesselField(context: RuleContext, path: string): unknown {
  return readPath(context.vesselSpec, path.split('.'));
}

/**
 * Reads the value a `reportFieldTruthy` or a `reportFieldEquals` node tests, so
 * the debug UI can show the input as well as the answer. It is undefined when
 * the path is not three parts, when a step is missing, or when the field holds
 * no value.
 *
 * `valueKey` reads one property of the value, for example the `code` of a
 * dropdown. It is undefined when the value holds no such property.
 *
 * The third part can name a cell of a row that holds a key, for example
 * `auxEngine3Current`. See `findCellOfKeyedRow`.
 */
export function readReportField(context: RuleContext, path: string, valueKey?: string): unknown {
  const keys = path.split('.');
  if (keys.length !== 3) {
    return undefined;
  }
  const [tabId, sectionId, fieldId] = keys;
  const section = readSection(context.formState, tabId, sectionId);
  const field = readPath(section, [fieldId]) ?? findCellOfKeyedRow(section, fieldId);
  const valueKeys = valueKey === undefined ? ['value'] : ['value', valueKey];
  return readPath(field, valueKeys);
}

/**
 * Finds a cell of a row that the vessel spec fixes, by its cell id. Such a row
 * holds a `key`, and the cell id is the key and the column, for example
 * `auxEngine3Current` for the `current` column of row `auxEngine3`.
 *
 * It finds the row by its key, not by its place in the array, because the
 * adapter skips an engine that holds no data. Undefined when no row holds a
 * key that starts the id, or when that row holds no such column.
 */
function findCellOfKeyedRow(section: unknown, cellId: string): unknown {
  if (section === null || typeof section !== 'object') {
    return undefined;
  }
  for (const rows of Object.values(section)) {
    if (!Array.isArray(rows)) {
      continue;
    }
    for (const row of rows) {
      const rowKey = readPath(row, ['key', 'value']);
      if (typeof rowKey !== 'string' || rowKey === '' || !cellId.startsWith(rowKey)) {
        continue;
      }
      const column = cellId.slice(rowKey.length);
      const cell = readPath(row, [column.charAt(0).toLowerCase() + column.slice(1)]);
      if (cell !== undefined) {
        return cell;
      }
    }
  }
  return undefined;
}

/**
 * Reads the value a field of the carry-over report holds, by the same
 * three-part path, so `carryOverField` and the debug UI read it one way.
 *
 * The carry-over report holds plain values, where the form state holds a field
 * with a `value` property, so this reads the value the path reaches and never
 * looks for `.value`. It is undefined when the path is not three parts, when a
 * step is missing, and when no carry-over report loads.
 *
 * A section that draws one table holds its columns in an array of rows, and
 * the path names a column as it names a field. While the context names a row,
 * a path that reaches no field reads that column at that row.
 *
 * The report can hold the section as the array of rows itself, with no key
 * around it. `consumption.fuelSpecs` is one: the form state holds its rows in
 * `fuelSpecs.entries`. The section is then the table.
 */
export function readCarryOverField(context: RuleContext, path: string): unknown {
  const keys = path.split('.');
  if (keys.length !== 3) {
    return undefined;
  }
  const value = readPath(context.carryOverReport, keys);
  if (value !== undefined || context.row === undefined) {
    return value;
  }
  const [tabId, sectionId, columnId] = keys;
  const section = readSection(context.carryOverReport, tabId, sectionId);
  const rows = Array.isArray(section) ? section : findTableOfColumn(section, columnId)?.rows;
  return readPath(rows?.[context.row], [columnId]);
}

/** Reads one section of a report root, keyed by tab and then section. */
export function readSection(root: unknown, tabId: string, sectionId: string): unknown {
  return readPath(root, [tabId, sectionId]);
}

/**
 * Finds the array of rows that holds a column, in a section that draws one
 * table. `tableId` is the key of the array in the section. Undefined when the
 * section holds the id as a field, and when no array holds it.
 */
export function findTableOfColumn(
  section: unknown,
  columnId: string
): { tableId: string; rows: unknown[] } | undefined {
  if (section === null || typeof section !== 'object' || Object.prototype.hasOwnProperty.call(section, columnId)) {
    return undefined;
  }
  for (const [tableId, entry] of Object.entries(section)) {
    if (Array.isArray(entry) && entry.some((row) => readPath(row, [columnId]) !== undefined)) {
      return { tableId, rows: entry };
    }
  }
  return undefined;
}

/**
 * Walks a list of keys from an object and returns the value at the end, or
 * undefined when a step is missing. It reads own keys only, so a crafted path
 * cannot reach the prototype chain.
 */
function readPath(source: unknown, keys: string[]): unknown {
  let value: unknown = source;
  for (const key of keys) {
    if (value === null || typeof value !== 'object') {
      return undefined;
    }
    const record: Record<string, unknown> = value as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      return undefined;
    }
    value = record[key];
  }
  return value;
}
